import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_ITEMS, FOUNDATION } from "@/lib/api";

export const Header = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white/90 backdrop-blur-sm"
      } border-b border-amber-200/70`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[70px]">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img
              src={FOUNDATION.logo}
              alt="Tourist Sandesh Foundation"
              className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-300 shadow-sm"
            />
            <div className="leading-tight">
              <div className="font-display text-[#0a2540] text-lg sm:text-xl">{FOUNDATION.name}</div>
              <div className="font-eng text-[10px] sm:text-[11px] tracking-wide text-amber-700 uppercase">
                {FOUNDATION.nameEng}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.path === "/" ? "home" : item.path.slice(1)}-link`}
                  className={`px-3 py-2 rounded-md text-[15px] font-medium transition-colors ${
                    active
                      ? "text-[#d97706] bg-amber-50"
                      : "text-[#0a2540] hover:text-[#d97706] hover:bg-amber-50/60"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="xl:hidden p-2 text-[#0a2540]"
            aria-label="मेनू"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="xl:hidden bg-white border-t border-amber-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col p-3">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.path === "/" ? "home" : item.path.slice(1)}-link`}
                  className={`px-4 py-3 rounded-md text-base font-medium ${
                    active ? "text-[#d97706] bg-amber-50" : "text-[#0a2540]"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};
